import type {ArtistCard, StoryArtist, ArtistListType, ArtistSong, ArtistCardSong, ArtistTypeStatus} from "@songfinch/types";

export const NormalizeStoryArtistToArtistCard = (
    artist: StoryArtist,
): ArtistCard => {
    const cardObject: ArtistCard = {
        id: artist.id.toString(),
        name: artist.fullName,
        photo: artist.photo,
        exclusivity: !!artist.exclusivity,
    };

    if (artist.city || artist.state) {
        cardObject.location = [artist.city, artist.state]
            .filter(Boolean)
            .join(", ");
    }

    if (artist.genres) {
        cardObject.genres = artist.genres.map((genre) => genre.name).join(", ");
    }

    return cardObject;
};

export const NormalizeArtistSong = (
    song: ArtistSong | undefined,
    artistName?: string,
): ArtistCardSong | undefined => {
    if (!song) return undefined;
    return {
        url: song.audio_url,
        artist: artistName,
        title: song.title,
        genres: song.genres,
    };
};

export const normalizeArtistQuickestDeliveryTime = (deliveryDayArray: string[]) => {
    return (deliveryDayArray).reduce((quickestTurnArround: number | undefined, deliveryDayAmount) => {
        const deliveryTime = Number(deliveryDayAmount) || undefined;
        if (!deliveryTime) return quickestTurnArround;
        if (!quickestTurnArround || quickestTurnArround > deliveryTime) return deliveryTime;
        return quickestTurnArround;
    }, undefined);
};


export const NormalizeArtistListTypeToArtistCard = (
    artist: ArtistListType,
): ArtistCard => { 
    const quickestDeliveryTime = normalizeArtistQuickestDeliveryTime(artist?.available_delivery_days || []);
    const availableDeliveryTime = quickestDeliveryTime ? `${quickestDeliveryTime * 24}hr` : undefined;

    return {
        id: artist.id.toString(),
        name: artist.artist_name,
        location: undefined,
        song: NormalizeArtistSong(artist.songs?.[0], artist.artist_name),
        genres: artist.genres ? Object.values(artist.genres).join(", ") : undefined,
        photo: artist.photo,
        availableDeliveryTime: availableDeliveryTime,
        exclusivity: !!artist.exclusivity,
        artistApplauseRating: artist.artist_applause_rating,
        songsCompleted: artist.songs_completed,
    };
};

const isStoryArtist = (artist: unknown): artist is StoryArtist =>
    !!(artist as StoryArtist).fullName;

const isArtistListType = (artist: unknown): artist is ArtistListType =>
    !!(artist as ArtistListType).artist_name;

export const normalizeArtistCard = (
    artist: StoryArtist | ArtistListType,
): ArtistCard | undefined => {
    if (isStoryArtist(artist)) {
        return NormalizeStoryArtistToArtistCard(artist);
    }
    if (isArtistListType(artist)) {
        return NormalizeArtistListTypeToArtistCard(artist);
    }
    return undefined;
};

export const hasActiveArtistBioPage = (status: ArtistTypeStatus) => {
    return ["active", "inactive", "at_capacity", "unresponsive"].includes(status);
};